'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@galapagos/design-system/web';
import { colors } from '@galapagos/design-system/colors';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useFinishSignupFlow } from '../hooks/useFinishSignupFlow';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useStepHeading } from '../hooks/useStepHeading';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { REGISTERED_CHECK_SIZE, ensureWrapUpStyle } from '../wrap-up/wrap-up-style';

function RegisteredStep() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const { t } = useTranslation('signup');
  const title = t('registered.title');
  const headingRef = useStepHeading(title);
  const resetFlow = useSignupFlowStore((state) => state.resetFlow);
  // The flow is over: the answers it collected leave memory.
  useEffect(() => {
    resetFlow();
  }, [resetFlow]);
  useFinishSignupFlow();
  ensureSignupStepStyle();
  ensureWrapUpStyle();
  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        {/* v9's green "done" panel: a large check, then the message as body text. */}
        <div
          className="signup-wrap-up__panel"
          data-tone="green"
          data-testid="signup-registered-panel"
        >
          <Icon
            name="tick-with-container"
            size={REGISTERED_CHECK_SIZE}
            color={colors.icon.primary}
            aria-hidden
          />
        </div>
        <p className="signup-step__body" data-testid="signup-registered-body">
          {t('registered.body')}
        </p>
        {/* The step's one status announcer; the flow has ended, so it stays empty. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status" />
      </div>
    </SignupShell>
  );
}

/**
 * `/registered/`: where an under-18 member's signup ends (production
 * "successfully registered"). No download step and no buttons: the care team
 * reaches out by email.
 */
export function RegisteredStepScreen() {
  return <RegisteredStep />;
}
